import React, {useState, useRef, useEffect, useMemo} from "react";
import PropTypes from "prop-types";
import {useTheme} from "../../theme/ThemeProvider";
import {buildStyles} from "../buildStyles";
import Container from "../layout/Container";

/**
 * DEFAULT_SETTINGS for all chart types
 *
 * height: Chart height in pixels
 * width: Chart width (accepts string % or px values, or number)
 * padding: Space around the chart content for axes and labels
 *   - top: Padding from the top of the chart container
 *   - right: Padding from the right side
 *   - bottom: Padding to accommodate x-axis labels
 *   - left: Padding to accommodate y-axis labels
 * tooltip: Controls for hover tooltips
 *   - enabled: Whether tooltips are shown on hover
 *   - component: Custom React component for tooltip rendering
 * grid: Controls for background grid lines
 *   - horizontal: Whether to show horizontal grid lines
 *   - vertical: Whether to show vertical grid lines
 *   - color: Grid line color (falls back to theme.colors.gray200)
 *   - dashArray: Pattern for dashed lines [line, gap]
 * axis: Configuration for x and y axes
 *   - x: Horizontal axis settings
 *     - visible: Whether the x-axis is shown
 *     - labelFontSize: Font size for tick labels
 *     - tickCount: Approximate number of ticks/labels to display
 *     - tickColor: Color of tick marks and axis line
 *   - y: Vertical axis settings
 *     - visible: Whether the y-axis is shown
 *     - labelFontSize: Font size for tick labels
 *     - tickCount: Number of horizontal grid lines and labels
 *     - tickColor: Color of tick marks and axis line
 */
const DEFAULT_SETTINGS = {
  height: 300,
  width: "100%",
  padding: {top: 20, right: 20, bottom: 30, left: 40},
  tooltip: {
    enabled: true,
    component: null,
  },
  grid: {
    horizontal: true,
    vertical: false,
    color: "",
    dashArray: [4, 4],
  },
  axis: {
    x: {
      visible: true,
      labelFontSize: 10,
      tickCount: 5,
      tickColor: "",
    },
    y: {
      visible: true,
      labelFontSize: 10,
      tickCount: 5,
      tickColor: "",
    },
  },
};

const useStyles = (theme) =>
  buildStyles({
    chartContainer: {
      backgroundColor: "transparent",
      position: "relative",
      width: "100%",
      height: "100%",
    },
    canvas: {
      width: "100%",
      height: "100%",
    },
  });

/**
 * BaseChart component that serves as a foundation for all chart types
 */
const BaseChart = ({
  data = [],
  series = [],
  settings = {},
  renderer: Renderer,
  ...rest
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const resizeTimeoutRef = useRef(null);

  const chartSettings = useMemo(
    () => ({
      ...DEFAULT_SETTINGS,
      ...settings,
      padding: {...DEFAULT_SETTINGS.padding, ...settings.padding},
      tooltip: {...DEFAULT_SETTINGS.tooltip, ...settings.tooltip},
      grid: {...DEFAULT_SETTINGS.grid, ...settings.grid},
      axis: {
        ...DEFAULT_SETTINGS.axis,
        ...settings.axis,
        x: {...DEFAULT_SETTINGS.axis.x, ...settings?.axis?.x},
        y: {...DEFAULT_SETTINGS.axis.y, ...settings?.axis?.y},
      },
    }),
    [settings],
  );

  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({x: 0, y: 0});
  const [hovering, setHovering] = useState(false);

  const handleMouseEnter = () => setHovering(true);

  const handleMouseLeave = () => {
    setHovering(false);
    setTooltipData(null);
  };

  const handleResize = () => {
    if (containerRef.current && canvasRef.current) {
      window.dispatchEvent(new Event("resize"));
    }
  };

  useEffect(() => {
    const debouncedResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(handleResize, 100);
    };

    window.addEventListener("resize", debouncedResize);

    return () => {
      window.removeEventListener("resize", debouncedResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  const renderTooltip = () => {
    if (!chartSettings.tooltip.enabled || !tooltipData || !hovering)
      return null;
    if (chartSettings.tooltip.component) {
      const TooltipComponent = chartSettings.tooltip.component;
      return (
        <TooltipComponent
          data={tooltipData}
          position={tooltipPosition}
          chartContainerRef={containerRef}
        />
      );
    }
    return null;
  };

  return (
    <Container
      className={classes.chartContainer}
      ref={containerRef}
      style={{height: chartSettings.height}}
      {...rest}
    >
      <canvas
        ref={canvasRef}
        className={classes.canvas}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />

      {renderTooltip()}

      {Renderer && (
        <Renderer
          canvasRef={canvasRef}
          containerRef={containerRef}
          chartSettings={chartSettings}
          hovering={hovering}
          setTooltipData={setTooltipData}
          setTooltipPosition={setTooltipPosition}
          data={data}
          series={series}
          theme={theme}
        />
      )}
    </Container>
  );
};

BaseChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  series: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      dataKey: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ),
  settings: PropTypes.object,
  renderer: PropTypes.func,
};

export default BaseChart;
